/* eslint-disable react/no-unstable-nested-components */
import React, { useCallback, useMemo } from 'react';

import { Box, Flex, Spacer, Typography } from '@aircall/tractor';
import { AddButton } from '@components/Button/AddButton';
import { Paper } from '@components/Paper/Paper';
import { HasAccess } from '@components/Permissions';
import { DataTable } from '@components/Table/DataTable';
import { ColumnProps } from '@components/Table/DataTable.decl';
import { TableDropdown } from '@components/Table/TableDropdown';
import { EmojiAvatar } from '@components/TeamAvatar/EmojiAvatar';
import { PERMISSIONS_ACTIONS, RESOURCE } from '@constants/permissions.constants';
import { TEAM_DETAIL_ROUTE } from '@constants/routes.constants';
import { routeReplacer } from '@dashboard/library';
import { TeamFragment } from '@generated/TeamFragment';
import { useAddUserToTeam } from '@hooks/useAddUserToTeam';
import { useToggle } from '@hooks/useToggle/useToggle';
import { AddTeamModal } from '@pages/number-detail-call-distribution/AddDispatchGroupSection/AddTeamModal';
import { UserProfileTabProps } from '@pages/user-profile/UserProfile.decl';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const TeamsSection = React.memo(({ user }: UserProfileTabProps) => {
  const { teams } = user;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [teamModalIsOpen, _, openTeamModal, closeTeamModal] = useToggle(false);
  const { addUserToTeam } = useAddUserToTeam();

  const handleAddTeam = useCallback(
    (team: TeamFragment) => {
      addUserToTeam(team, user);
    },
    [addUserToTeam, user]
  );

  /** Columns definitions for the table */
  const tableColumns: ColumnProps<TeamFragment>[] = useMemo(
    () => [
      {
        name: t('user_profile.numbers_and_teams.associated_teams.table.columns.name.title'),
        width: '304px',
        cellRenderer: ({ datum }) => {
          const emoji = datum.imageName;

          return (
            <Flex alignItems='center'>
              {emoji && (
                <Box mr='xxs'>
                  <EmojiAvatar emoji={emoji} size='medium' />
                </Box>
              )}
              <Typography variant='body' ellipsis>
                {datum?.name}
              </Typography>
            </Flex>
          );
        },
      },
      {
        name: t('user_profile.numbers_and_teams.associated_teams.table.columns.users.title'),
        cellRenderer: ({ datum }) => (
          <Typography variant='body'>
            {t('user_profile.numbers_and_teams.associated_teams.table.columns.users.count', {
              count: datum?.agentsIds?.length || 0,
            })}
          </Typography>
        ),
      },
      {
        name: '',
        width: '40px',
        cellRenderer: ({ datum, rowIndex }) => (
          <TableDropdown
            options={[
              {
                title: t('user_profile.numbers_and_teams.associated_teams.tags.go_to_team_page'),
                callback: () => {
                  navigate(routeReplacer(TEAM_DETAIL_ROUTE, { teamId: datum.ID }));
                },
              },
            ]}
            data-test={`associated-teams-table-dropdown-${rowIndex}`}
          />
        ),
      },
    ],
    [navigate, t]
  );

  const teamsIds = (teams || []).map(({ ID }) => ID);
  const teamsIdsBlacklist = new Set(teamsIds);

  return (
    <Paper title={t('user_profile.numbers_and_teams.associated_teams.title')} mb={48}>
      <AddTeamModal
        onAdd={handleAddTeam}
        idsBlacklist={teamsIdsBlacklist}
        isOpen={teamModalIsOpen}
        onClose={closeTeamModal}
      />
      <Spacer direction='vertical' space='s'>
        <DataTable
          data={teams}
          data-test='associated-teams-table'
          emptyMessage={t('user_profile.numbers_and_teams.associated_teams.table.empty')}
          columns={tableColumns}
        />
        <HasAccess resource={RESOURCE.TEAMS} action={PERMISSIONS_ACTIONS.CREATE}>
          <AddButton
            data-test='add-associated-team-button'
            hasData={!!teams?.length}
            onClick={openTeamModal}
            translationKey='generic.add_team'
          />
        </HasAccess>
      </Spacer>
    </Paper>
  );
});
